import React, { useState, useEffect, memo } from "react";
import moment from "moment";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { Minimize as MinimizeIcon, Close as CloseIcon} from '@material-ui/icons';
import { Link } from "react-router-dom";

import ModalContainer from "../../../base/ModalContainer";
import PrimaryButton from "../../common/PrimaryButton/PrimaryButton";

import { userStyles } from "../Styles/RenderPolygonDetailsPopUpStyles";
import { createCamelCase } from "../../../utils/helperFunctions";
import { POLYGON_CONSTANT_TEXT } from "../Utils/PlolygonServicesConstants";
import { isArrayValid, isEmptyOrNull } from "../../../utils/DataUtils";
import { isMultipleConfigurationAllowed } from "../Utils/PlolygonServicesUtils";
import {
  MappingListItem,
  MappingListPosZoneArray,
  PolygonPopUpDetailsInterface,
  PolygonServiceRecordInterface,
  ServiceTypeRecord,
} from "../Utils/PlolygonServicesInterface";
import { APP_ROUTES } from "config/constants";

// Moved props interface outside the component for better readability
interface RenderPolygonDetailsPopUpInterface {
  open: boolean;
  handleClose: () => void;
  polygonDetailsPopUpData: PolygonPopUpDetailsInterface;
  polygonServiceRecord: PolygonServiceRecordInterface[];
  showEditButton?: boolean;
  isFromMapView?: boolean;
}

// A more robust way to define the props for the memoized component
interface ServiceMappingDisplayProps {
  mappingObj: MappingListItem;
  isMultipleConfiguration: boolean;
  posZoneMappingArray?: MappingListPosZoneArray[];
  classes: ReturnType<typeof userStyles>;
  polygonId: string;
}

const ServiceMappingDisplay: React.FC<ServiceMappingDisplayProps> = memo(
  ({ mappingObj, isMultipleConfiguration, posZoneMappingArray, classes, polygonId }) => {
    const generatePosZoneAreaText = (mappingObj: Partial<MappingListPosZoneArray>) => {
      const parts = [];
      if (mappingObj?.posNo) {
        parts.push(mappingObj.posNo);
      }
      if (mappingObj?.posName) {
        parts.push(mappingObj.posName);
      }
      if (mappingObj?.zoneName) {
        parts.push(mappingObj.zoneName);
      }
      const areaText = parts.join(" - ");

      return (
        <Typography className={classes.configurationValueServiceTextStyle}>
          {areaText}
        </Typography>
      );
    };

    const formattedStartTime = moment(mappingObj.startTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT).isValid()
      ? moment(mappingObj.startTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT).format(POLYGON_CONSTANT_TEXT.VIEW_TIME_FORMAT)
      : "-";

    const formattedEndTime = moment(mappingObj.endTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT).isValid()
      ? moment(mappingObj.endTime, POLYGON_CONSTANT_TEXT.BACK_END_TIME_FORMAT).format(POLYGON_CONSTANT_TEXT.VIEW_TIME_FORMAT)
      : "-";

    const renderPolygonDataDetails = () => {
      if (!mappingObj?.isNonServiceable) {
        return generatePosZoneAreaText(mappingObj);
      }

      return (
        <Typography className={classes.configurationValueNonServiceTextStyle}>
          {POLYGON_CONSTANT_TEXT.NON_SERVICEABLE_AREA}
        </Typography>
      );
    };

    return (
      <Grid container spacing={1} className={classes.configurationTimeDetailsGridContainer}>
        <Grid item xs={1} className={isMultipleConfiguration ? classes.configurationGridHeaderItemStyle : classes.marginAuto}>
          <Typography className={classes.configurationValueTextStyle}>
            {formattedStartTime}
          </Typography>
        </Grid>
        <Grid item xs={1} className={isMultipleConfiguration ? classes.configurationGridHeaderItemStyle : classes.marginAuto}>
          <Typography className={classes.configurationValueTextStyle}>
            {formattedEndTime}
          </Typography>
        </Grid>
        <Grid item className={isMultipleConfiguration ? classes.configurationGridHeaderItemStyle : classes.marginAuto}>
          {isMultipleConfiguration && isArrayValid(posZoneMappingArray)
            ? posZoneMappingArray?.map((posZoneMapping, posZoneMappingIndex) =>
              !posZoneMapping.isNonServiceable ? (
                <React.Fragment key={`posZoneMapping-${posZoneMappingIndex}-${posZoneMapping?.posNo ?? ""}`}>
                  {generatePosZoneAreaText(posZoneMapping)}
                </React.Fragment>
              ) : (
                <Typography
                  key={`posZoneMapping-${posZoneMappingIndex}-No-Serviceable Area-${polygonId ?? ""}`}
                  className={classes.configurationValueNonServiceTextStyle}
                >
                  {POLYGON_CONSTANT_TEXT.NON_SERVICEABLE_AREA}
                </Typography>
              )
            )
            : renderPolygonDataDetails()}
        </Grid>
      </Grid>
    );
  }
);


const RenderPolygonDetailsPopUp = (props: RenderPolygonDetailsPopUpInterface) => {
  const { open, handleClose, polygonDetailsPopUpData, polygonServiceRecord, showEditButton, isFromMapView } = props;
  const classes = userStyles();

  const [minimized, setMinimized] = useState(false);
  const resolveInitialTab = (preSelected?: string): string => {
    if (preSelected && isArrayValid(polygonServiceRecord)) {
      const match = polygonServiceRecord?.find((s: PolygonServiceRecordInterface) => s?.name?.toUpperCase() === preSelected.toUpperCase());
      if (match) return match.name;
    }
    return isArrayValid(polygonServiceRecord) ? polygonServiceRecord?.[0]?.name ?? "" : "";
  };
  const [tabValue, setTabValue] = useState(() => resolveInitialTab(polygonDetailsPopUpData?.preSelectedServiceType));

  useEffect(() => {
    if (open) {
      setMinimized(false);
      setTabValue(resolveInitialTab(polygonDetailsPopUpData?.preSelectedServiceType));
    }
  }, [open, polygonDetailsPopUpData?.polygonId, polygonDetailsPopUpData?.preSelectedServiceType, polygonServiceRecord]);

  const handleMinimize = () => {
    setMinimized(true);
  };

  const handleRestore = () => {
    setMinimized(false);
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTabValue(newValue);
  };

  const selectedServiceData = polygonDetailsPopUpData?.serviceTypeRecordList.find((service: ServiceTypeRecord) => service?.name?.toUpperCase() === tabValue.toUpperCase());

  const renderNoDataAvailable = () => {
    return (
      <Card className={classes.nonDataPresentCard}>
        <CardContent className={classes.nonDataPresentCardContent}>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography className={classes.nonDataPresentCardSpanStyle}>{`No data available`}</Typography>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderPolygonDetailsConfigurationDetails = (serviceType: string, mappings: MappingListItem[]) => {
    const polygonServiceTypeRecord = polygonDetailsPopUpData?.serviceTypeRecordList.find((service: ServiceTypeRecord) => service?.name?.toUpperCase() === serviceType.toUpperCase());
    const isMultipleConfiguration: boolean = isMultipleConfigurationAllowed(polygonServiceTypeRecord?.multiPolygonEnabled);
    if (!isMultipleConfiguration) {
      return (
        <>
          {mappings?.map((mappingObj, mappingObjIndex) => (
            <ServiceMappingDisplay
              key={`mapping-single-${mappingObjIndex}`}
              mappingObj={mappingObj}
              isMultipleConfiguration={false}
              classes={classes}
              polygonId={polygonDetailsPopUpData?.polygonId ?? ""}
            />
          ))}
        </>
      );
    }

    return (
      <>
        {mappings?.map((mappingObj, mappingObjIndex) => (
          <ServiceMappingDisplay
            key={`mapping-single-${mappingObjIndex}`}
            mappingObj={mappingObj}
            isMultipleConfiguration={true}
            posZoneMappingArray={mappingObj.posZoneMappingArray}
            classes={classes}
            polygonId={polygonDetailsPopUpData?.polygonId ?? ""}
          />
        ))}
      </>
    );
  };

  const renderServiceDetails = () => {
    if (isEmptyOrNull(selectedServiceData)) {
      return renderNoDataAvailable();
    }

    if (!isArrayValid(selectedServiceData?.mappingList)) {
      return renderNoDataAvailable();
    }

    return (
      <Box className={classes.configDetailsBox}>
        {selectedServiceData?.mappingList?.map((mappingItem, mappingItemIndex) => (
          <Grid key={`mapping-item-${mappingItemIndex}`} container spacing={0} className={classes.configDetailsGridContainer}>
            <Grid item className={classes.configDetailsGridItem}>
              <Typography className={classes.configurationHeaderTextStyle}>{`${POLYGON_CONSTANT_TEXT.DAYS} : `}</Typography>
            </Grid>
            <Grid item className={classes.configDetailsGridItem}>
              <Typography className={classes.configurationValueTextStyle}>{mappingItem?.days?.join(", ")}</Typography>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={1} className={classes.marginAuto}>
                <Typography className={classes.configurationHeaderTextStyle}>{`${POLYGON_CONSTANT_TEXT.START_TIME}`}</Typography>
              </Grid>
              <Grid item xs={1} className={classes.marginAuto}>
                <Typography className={classes.configurationHeaderTextStyle}>{`${POLYGON_CONSTANT_TEXT.END_TIME}`}</Typography>
              </Grid>
              <Grid item className={classes.marginAuto}>
                <Typography className={classes.configurationHeaderTextStyle}>{`${POLYGON_CONSTANT_TEXT.POS_ZONE}`}</Typography>
              </Grid>
              {renderPolygonDetailsConfigurationDetails(tabValue, mappingItem.mappings)}
            </Grid>
          </Grid>
        ))
        }
      </Box>
    )
  }

  if (minimized && open) {
    return (
      <Paper
        className={classes.minimizedContainer}
        onClick={handleRestore}
        elevation={6}
      >
        <Typography variant="body2" className={classes.minimizedTitle}>
          {polygonDetailsPopUpData?.polygonId ? `Polygon ID: ${polygonDetailsPopUpData.polygonId}` : "Polygon Details"}
        </Typography>
      </Paper>
    );
  }

  return (
    <ModalContainer open={open} onClose={handleClose}>
      <Card className={classes.cardContainer}>
        <CardContent className={classes.cardContentStyle}>
          <Grid container spacing={0} className={classes.gridContainerMain} justifyContent="space-between" alignItems="center">
            <Grid item xs={6} className={classes.gridItemTitleDivStyle}>
              <Typography className={classes.headerSpanStyle}>
                {"POLYGON ID" +
                  (!isEmptyOrNull(polygonDetailsPopUpData?.polygonId)
                    ? ": " + polygonDetailsPopUpData.polygonId
                    : ""
                  )}
              </Typography>
            </Grid>
            <Grid item xs={6} className={classes.gridItemServiceableDivStyle}>
              {isFromMapView && (
                <IconButton size="small" onClick={handleMinimize} className={classes.iconButtonStyle}>
                  <MinimizeIcon className={classes.minimiseButtonStyle} />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleClose} className={classes.iconButtonStyle}>
                <CloseIcon className={classes.minimiseButtonStyle} />
              </IconButton>
            </Grid>
          </Grid>

          {isArrayValid(polygonServiceRecord) && isArrayValid(polygonDetailsPopUpData?.serviceTypeRecordList) ? (
            <Tabs
              value={tabValue}
              onChange={handleChange}
              variant="fullWidth"
              aria-label="Service tabs"
              className={classes.tabsStyle}
            >
              {polygonServiceRecord.map((serviceObj: PolygonServiceRecordInterface) => (
                <Tab
                  key={serviceObj.name}
                  label={serviceObj?.displayName ?? createCamelCase(serviceObj.name)}
                  value={serviceObj.name}
                  className={classes.tabTextStyle}
                />
              ))}
            </Tabs>
          ) : (
            renderNoDataAvailable()
          )}

          {tabValue ? renderServiceDetails() : (<div>Please select a service tab.</div>)}

          <Grid container className={classes.gridContainerFooter} spacing={1}>
            <Grid item className={classes.marginAuto}>
              <Grid container spacing={1}>
                {showEditButton && (
                  <Link
                    to={{
                      pathname: `${APP_ROUTES.POLYGON_SERVICES_CONFIGURATION}`,
                      search: `?${POLYGON_CONSTANT_TEXT.POLYGON_ID_PARAM}=${polygonDetailsPopUpData?.polygonId}`,
                    }}
                    className={classes.linkStyle}
                  >
                    <PrimaryButton buttonLabel="Edit Polygon" />
                  </Link>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </ModalContainer>
  );
};

export default React.memo(RenderPolygonDetailsPopUp);